import axios from 'axios';

class TwitterAuthService {
  constructor() {
    this.accessToken = null;
    this.userTokens = {};
  }

  async initializeAuth() {
    const clientId = process.env.REACT_APP_TWITTER_CLIENT_ID;
    const bearerToken = process.env.REACT_APP_TWITTER_BEARER_TOKEN;
    
    if (!clientId || clientId === 'your_twitter_client_id_here') {
      throw new Error('Twitter Client ID not configured. Please set REACT_APP_TWITTER_CLIENT_ID in your .env file.');
    }
    
    const redirectUri = `${window.location.origin}/auth/twitter/callback`;
    const codeChallenge = this.generateCodeChallenge();
    
    // Store code verifier for callback
    localStorage.setItem('twitter_code_verifier', codeChallenge.verifier);
    
    const scopes = [
      'tweet.read',
      'tweet.write',
      'users.read',
      'follows.read',
      'follows.write',
      'offline.access',
      'space.read',
      'mute.read',
      'mute.write',
      'like.read',
      'like.write',
      'list.read',
      'list.write',
      'block.read',
      'block.write',
      'bookmark.read',
      'bookmark.write'
    ].join(' ');

    const authUrl = `https://twitter.com/i/oauth2/authorize?` +
      `response_type=code&` +
      `client_id=${clientId}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `scope=${encodeURIComponent(scopes)}&` +
      `state=${this.generateState()}&` +
      `code_challenge=${codeChallenge.challenge}&` +
      `code_challenge_method=S256`;

    return {
      authUrl,
      clientId,
      redirectUri,
      bearerToken: bearerToken
    };
  }

  generateCodeChallenge() {
    const verifier = this.generateRandomString(128);
    const challenge = btoa(verifier)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
    
    return {
      verifier,
      challenge
    };
  }

  generateRandomString(length) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  generateState() {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  async handleAuthCallback(code, state) {
    try {
      const clientId = process.env.REACT_APP_TWITTER_CLIENT_ID;
      const clientSecret = process.env.REACT_APP_TWITTER_CLIENT_SECRET;
      const redirectUri = `${window.location.origin}/auth/twitter/callback`;
      const codeVerifier = localStorage.getItem('twitter_code_verifier');

      if (!codeVerifier) {
        throw new Error('Code verifier not found');
      }

      const tokenResponse = await axios.post('https://api.twitter.com/2/oauth2/token', 
        new URLSearchParams({
          code: code,
          grant_type: 'authorization_code',
          client_id: clientId,
          redirect_uri: redirectUri,
          code_verifier: codeVerifier
        }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Authorization': `Basic ${btoa(`${clientId}:${clientSecret}`)}`
          }
        }
      );

      this.accessToken = tokenResponse.data.access_token;
      const refreshToken = tokenResponse.data.refresh_token;

      // Store tokens securely
      const tokens = {
        access_token: this.accessToken,
        refresh_token: refreshToken,
        expires_in: tokenResponse.data.expires_in,
        token_type: tokenResponse.data.token_type,
        scope: tokenResponse.data.scope
      };

      localStorage.setItem('twitter_tokens', JSON.stringify(tokens));
      localStorage.removeItem('twitter_code_verifier');

      return tokens;
    } catch (error) {
      console.error('Twitter Auth callback error:', error);
      throw error;
    }
  }

  async getUserProfile() {
    try {
      if (!this.accessToken) {
        const storedTokens = localStorage.getItem('twitter_tokens');
        if (storedTokens) {
          const tokens = JSON.parse(storedTokens);
          this.accessToken = tokens.access_token;
        } else {
          throw new Error('Twitter access token not found');
        }
      }

      const response = await axios.get('https://api.twitter.com/2/users/me', {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`
        },
        params: {
          'user.fields': 'created_at,description,entities,id,location,name,pinned_tweet_id,profile_image_url,protected,public_metrics,url,username,verified,verified_type'
        }
      });

      return this.formatUserProfile(response.data.data);
    } catch (error) {
      console.error('Error fetching Twitter user profile:', error);
      throw error;
    }
  }

  formatUserProfile(user) {
    return {
      id: user.id,
      username: user.username,
      name: user.name,
      description: user.description,
      location: user.location,
      profileImageUrl: user.profile_image_url,
      url: user.url,
      verified: user.verified,
      followers: user.public_metrics?.followers_count || 0,
      following: user.public_metrics?.following_count || 0,
      tweets: user.public_metrics?.tweet_count || 0,
      listed: user.public_metrics?.listed_count || 0,
      createdAt: user.created_at,
      protected: user.protected,
      source: 'twitter'
    };
  }

  async getTweets(userId, maxResults = 10) {
    try {
      if (!this.accessToken) {
        const storedTokens = localStorage.getItem('twitter_tokens');
        if (storedTokens) {
          const tokens = JSON.parse(storedTokens);
          this.accessToken = tokens.access_token;
        } else {
          throw new Error('Twitter access token not found');
        }
      }

      const response = await axios.get(`https://api.twitter.com/2/users/${userId}/tweets`, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`
        },
        params: {
          max_results: maxResults,
          'tweet.fields': 'created_at,author_id,context_annotations,conversation_id,entities,geo,id,in_reply_to_user_id,lang,public_metrics,referenced_tweets,reply_settings,source,text,withheld',
          'user.fields': 'created_at,description,entities,id,location,name,pinned_tweet_id,profile_image_url,protected,public_metrics,url,username,verified',
          expansions: 'author_id,attachments.media_keys,referenced_tweets.id,referenced_tweets.id.author_id',
          'media.fields': 'duration_ms,height,media_key,preview_image_url,type,url,width,public_metrics'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching tweets:', error);
      return [];
    }
  }

  async postTweet(text, mediaIds = []) {
    try {
      if (!this.accessToken) {
        throw new Error('Twitter access token not found');
      }

      const tweetData = { text };
      if (mediaIds.length > 0) {
        tweetData.media = { media_ids: mediaIds };
      }

      const response = await axios.post('https://api.twitter.com/2/tweets', tweetData, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Error posting tweet:', error);
      throw error;
    }
  }

  async uploadMedia(file) {
    try {
      if (!this.accessToken) {
        throw new Error('Twitter access token not found');
      }

      const formData = new FormData();
      formData.append('media', file);

      const response = await axios.post('https://upload.twitter.com/1.1/media/upload.json', formData, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      return response.data.media_id_string;
    } catch (error) {
      console.error('Error uploading media:', error);
      throw error;
    }
  }

  async refreshTokens() {
    try {
      const storedTokens = localStorage.getItem('twitter_tokens');
      if (!storedTokens) {
        throw new Error('No tokens found');
      }

      const tokens = JSON.parse(storedTokens);
      const refreshToken = tokens.refresh_token;

      if (!refreshToken) {
        throw new Error('No refresh token found');
      }

      const clientId = process.env.REACT_APP_TWITTER_CLIENT_ID;
      const clientSecret = process.env.REACT_APP_TWITTER_CLIENT_SECRET;

      const response = await axios.post('https://api.twitter.com/2/oauth2/token', 
        new URLSearchParams({
          refresh_token: refreshToken,
          grant_type: 'refresh_token',
          client_id: clientId
        }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Authorization': `Basic ${btoa(`${clientId}:${clientSecret}`)}`
          }
        }
      );

      const newTokens = {
        access_token: response.data.access_token,
        refresh_token: response.data.refresh_token,
        expires_in: response.data.expires_in,
        token_type: response.data.token_type,
        scope: response.data.scope
      };

      this.accessToken = newTokens.access_token;
      localStorage.setItem('twitter_tokens', JSON.stringify(newTokens));

      return newTokens;
    } catch (error) {
      console.error('Error refreshing Twitter tokens:', error);
      this.clearTokens();
      throw error;
    }
  }

  clearTokens() {
    localStorage.removeItem('twitter_tokens');
    localStorage.removeItem('twitter_code_verifier');
    this.accessToken = null;
    this.userTokens = {};
  }

  isAuthenticated() {
    const tokens = localStorage.getItem('twitter_tokens');
    return !!tokens;
  }
}

export default TwitterAuthService;